import clsx from 'clsx';
import type { ReactNode } from 'react';

import { HERO_VARIANT } from '../../hooks/GetHero.ts';

export type SectionBackground =
  | 'default'
  | 'contrast'
  | 'paper'
  | 'primary'
  | 'secondary'
  | 'light-gray'
  | `gradient-${typeof HERO_VARIANT.BLUE}`
  | `gradient-${typeof HERO_VARIANT.RED}`
  | `gradient-${typeof HERO_VARIANT.YELLOW}`;

export type SectionProps = {
  children?: ReactNode;
  className?: string;
  fullWidth?: boolean;
  overflow?: 'visible' | 'hidden';
  last?: boolean;
  fullHeight?: boolean;
  extraPadding?: boolean;
  background?: SectionBackground;
} & Omit<React.ComponentPropsWithoutRef<'section'>, 'className'>;

const backgroundClasses = {
  contrast: 'bg-v1-background-200',
  default: 'bg-v1-background-60',
  paper: 'bg-white',
  primary: 'bg-v1-primary-100',
  secondary: 'bg-v1-secondary-100',
  'light-gray': 'bg-v1-background-20',
  'gradient-blue': clsx(
    'bg-[radial-gradient(98.73%_52.1%_at_47.33%_51.23%,rgba(255,255,255,0.49)_0%,rgba(255,255,255,0)_97.5%),#18a0fb]',
    'md:bg-[radial-gradient(61.37%_762.47%_at_80.21%_50%,rgba(255,255,255,0.46)_0%,rgba(255,255,255,0.01)_67.16%,rgba(255,255,255,0.01)_100%),#18a0fb]',
  ),
  'gradient-yellow': clsx(
    'bg-[radial-gradient(98.73%_52.1%_at_47.33%_51.23%,rgba(255,255,255,0.49)_0%,rgba(255,255,255,0)_97.5%),#fdc564]',
    'md:bg-[radial-gradient(61.37%_762.47%_at_80.21%_50%,rgba(255,255,255,0.46)_0%,rgba(255,255,255,0.01)_67.16%,rgba(255,255,255,0.01)_100%),#fdc564]',
  ),
  'gradient-red': clsx(
    'bg-[radial-gradient(98.73%_52.1%_at_47.33%_51.23%,rgba(255,255,255,0.49)_0%,rgba(255,255,255,0)_97.5%),#fa4969]',
    'md:bg-[radial-gradient(61.37%_762.47%_at_80.21%_50%,rgba(255,255,255,0.46)_0%,rgba(255,255,255,0.01)_67.16%,rgba(255,255,255,0.01)_100%),#fa4969]',
  ),
} satisfies Record<SectionBackground, string>;

const getPaddingClasses = ({
  fullHeight,
  fullWidth,
  last,
  extraPadding,
}: Pick<SectionProps, 'fullHeight' | 'fullWidth' | 'last' | 'extraPadding'>) => {
  if (fullHeight) return 'p-0';

  const horizontal = fullWidth ? 'px-0' : 'px-8 md:px-20';

  if (extraPadding) {
    return clsx(
      'pt-20 sm:pt-24 md:pt-28',
      horizontal,
      last ? 'pb-36 sm:pb-44 md:pb-[200px] lg:pb-24' : 'pb-20 sm:pb-24 md:pb-36 lg:pb-36',
    );
  }

  return clsx(
    'pt-8 sm:pt-12 md:pt-16',
    horizontal,
    last ? 'pb-36 sm:pb-44 md:pb-[200px] lg:pb-24' : 'pb-8 sm:pb-12 md:pb-16 lg:pb-16',
  );
};

/** @deprecated Avoid using this component in new code */
const Section = ({
  children,
  background = 'default',
  overflow = 'hidden',
  fullWidth,
  fullHeight,
  last,
  extraPadding,
  className,
  ...props
}: SectionProps) => {
  if (!children) return null;

  return (
    <section
      className={clsx(
        'relative flex w-full flex-col items-center',
        overflow === 'visible' ? 'overflow-visible' : 'overflow-hidden',
        fullWidth ? '*:max-w-none' : '*:max-w-[1200px]',
        overflow === 'visible' ? '*:z-1050' : '*:z-2',
        backgroundClasses[background],
        getPaddingClasses({ fullHeight, fullWidth, last, extraPadding }),
        className,
      )}
      {...props}
    >
      {children}
    </section>
  );
};

export default Section;
